import { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { UserRound } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { STEP_META, TOTAL_STEPS } from "../types";
import { gradientBrand } from "../theme";
import { useAuth } from "../routing/AuthContext";
import { ROUTES } from "../routing/paths";

interface WizardHeaderProps {
  step: number;
}

export default function WizardHeader({ step }: WizardHeaderProps) {
  const navigate = useNavigate();
  const { session, logout, properties } = useAuth();
  const [accountOpen, setAccountOpen] = useState(false);

  const meta = STEP_META[step - 1];
  const progressPct = (step / TOTAL_STEPS) * 100;
  const userName = session?.user.name?.trim() || "Usuario";
  const userEmail = session?.user.email?.trim() || "";

  const handleLogout = () => {
    setAccountOpen(false);
    logout();
    navigate(ROUTES.login, { replace: true });
  };

  const handleGoToMenu = () => {
    setAccountOpen(false);
    navigate(ROUTES.menu);
  };

  const showMenuOption = properties.length > 0;

  return (
    <>
      <Box
        sx={{
          background: gradientBrand,
          px: { xs: 2.5, md: 5 },
          py: 3,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "flex-end" },
          justifyContent: "space-between",
          gap: 3,
          color: "#fff",
        }}
      >
        <Box
          sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}
        >
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              opacity: 0.85,
            }}
          >
            {meta.label}
          </Typography>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: 22, md: 28 },
              lineHeight: 1.15,
            }}
          >
            {meta.title}
          </Typography>
          <Typography
            sx={{ fontSize: 14, lineHeight: 1.5, maxWidth: 640, opacity: 0.92 }}
          >
            {meta.desc}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 1.25,
            flex: "none",
            alignSelf: { xs: "flex-end", sm: "auto" },
          }}
        >
          <IconButton
            aria-label="Cuenta de usuario"
            onClick={() => setAccountOpen(true)}
            sx={{
              color: "#fff",
              border: "1px solid rgba(255,255,255,.45)",
              background: "rgba(255,255,255,.12)",
              width: 40,
              height: 40,
              "&:hover": {
                background: "rgba(255,255,255,.22)",
              },
            }}
          >
            <UserRound size={20} />
          </IconButton>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Typography sx={{ fontSize: 12, opacity: 0.9 }}>
              {step}/{TOTAL_STEPS}
            </Typography>
            <Box
              sx={{
                width: 160,
                height: 6,
                borderRadius: 99,
                background: "rgba(255,255,255,.32)",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  background: "#fff",
                  borderRadius: 99,
                  width: `${progressPct}%`,
                  transition: "width .22s cubic-bezier(.22,1,.36,1)",
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      <Dialog
        open={accountOpen}
        onClose={() => setAccountOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Mi cuenta</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontWeight: 700, fontSize: 18, mb: 0.5 }}>
            {userName}
          </Typography>
          {userEmail ? (
            <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
              {userEmail}
            </Typography>
          ) : null}
          {showMenuOption ? (
            <Button
              fullWidth
              variant="outlined"
              onClick={handleGoToMenu}
              sx={{ mt: 2.5 }}
            >
              Regresar al menú
            </Button>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setAccountOpen(false)}>Cancelar</Button>
          <Button onClick={handleLogout} variant="contained" color="error">
            Cerrar sesión
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
